/**
 * MuiOrdersList - Optimized Orders List Page
 *
 * OPTIMIZATION NOTES (Phase 1.1):
 * - Uses server-side pagination via usePaginatedData hook
 * - Filters (status) are applied server-side via FlexFlow filter param
 * - Search is debounced (300ms) to prevent excessive API calls
 * - Sorting is done server-side
 * - Analytics panel fetches counts separately for performance
 * - Original functionality preserved: status chips, date search, charts
 *
 * @see OPTIMIZATION_PLAN.md for full details
 */

import React, { useEffect, useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"

// MUI Components
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Stack,
  Breadcrumbs,
  Link,
  Collapse,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { Link as RouterLink } from "react-router-dom"

// MUI Icons
import RefreshIcon from "@mui/icons-material/Refresh"
import SearchIcon from "@mui/icons-material/Search"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import HomeIcon from "@mui/icons-material/Home"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import BarChartIcon from "@mui/icons-material/BarChart"
import ClearIcon from "@mui/icons-material/Clear"

// Recharts
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"

// Custom MUI Components
import MuiOrdersTable from "./MuiOrdersTable"

// Optimized API Helpers
import { getOrdersPaginated, fetchPaginated } from "helpers/api_helper"
import { GET_ORDERS } from "helpers/api_urls"

// Custom Hook for server-side pagination
import usePaginatedData from "hooks/usePaginatedData"

// Status configuration
const STATUS_CONFIG = {
  "PENDING-STOCK": { label: "Pending Stock", color: "#f59e0b" },
  "STOCK-OK": { label: "Stock OK", color: "#3b82f6" },
  "READY-TO-INVOICE": { label: "Ready to Invoice", color: "#8b5cf6" },
  "ON SHIPPING": { label: "On Shipping", color: "#f97316" },
  INVOICED: { label: "Invoiced", color: "#06b6d4" },
  OFFER: { label: "Offers", color: "#6366f1" },
  SHIPPED: { label: "Shipped", color: "#10b981" },
  RETURNED: { label: "Returns", color: "#ef4444" },
  All: { label: "All", color: "#6b7280" },
}

const ALL_STATUSES = [
  "PENDING-STOCK",
  "STOCK-OK",
  "READY-TO-INVOICE",
  "ON SHIPPING",
  "INVOICED",
  "OFFER",
  "SHIPPED",
  "RETURNED",
  "All",
]

/**
 * Fetch status counts for analytics (lightweight - no full records)
 * This replaces fetching all orders just for counting
 */
async function fetchStatusCounts() {
  const counts = {}

  // Fetch counts for each status in parallel
  const promises = ALL_STATUSES.filter((s) => s !== "All").map(async (status) => {
    try {
      const result = await fetchPaginated(GET_ORDERS, {
        limit: 0, // Don't fetch actual records
        filter: `status=${status}`,
      })
      counts[status] = result.total
    } catch (error) {
      console.error(`Error fetching count for ${status}:`, error)
      counts[status] = 0
    }
  })

  // Also fetch total count (excluding OFFER-DELETE)
  promises.push(
    (async () => {
      try {
        const result = await fetchPaginated(GET_ORDERS, {
          limit: 0,
          filter: "status!=OFFER-DELETE",
        })
        counts["All"] = result.total
      } catch (error) {
        counts["All"] = 0
      }
    })()
  )

  await Promise.all(promises)
  return counts
}

/**
 * Fetch orders for delivery timeline analytics
 * Only fetches active orders (not SHIPPED, RETURNED, OFFER) with delivery dates
 * Limited to essential fields for performance
 */
async function fetchDeliveryTimelineData() {
  try {
    const result = await fetchPaginated(GET_ORDERS, {
      limit: 1000, // Reasonable limit for timeline calculation
      filter:
        "(status!=SHIPPED) AND (status!=RETURNED) AND (status!=OFFER) AND (status!=OFFER-DELETE)",
      orderBy: "expected_delivery_date",
      orderDir: "ASC",
    })
    return result.data
  } catch (error) {
    console.error("Error fetching delivery timeline data:", error)
    return []
  }
}

const MuiOrdersList = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  document.title = `${t("Orders")} | FlexFlow ERP`

  // Local state for UI
  const [statusFilter, setStatusFilter] = useState("PENDING-STOCK")
  const [fromDate, setFromDate] = useState(moment().subtract(7, "days"))
  const [toDate, setToDate] = useState(moment())
  const [chartsExpanded, setChartsExpanded] = useState(false)

  // Analytics state (fetched separately for performance)
  const [statusCounts, setStatusCounts] = useState({})
  const [deliveryData, setDeliveryData] = useState([])
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  // Date range filter state
  const [dateRangeFilter, setDateRangeFilter] = useState(null)

  // ============================================================================
  // SERVER-SIDE PAGINATED DATA
  // ============================================================================
  // This replaces the old getOrders() that fetched ALL records
  const {
    data: orders,
    total,
    loading,
    error,
    pagination,
    searchTerm,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleFilterChange,
    handleSortChange,
    refresh,
    sortModel,
  } = usePaginatedData(getOrdersPaginated, {
    initialPageSize: 50,
    initialFilters: { statusFilter: "PENDING-STOCK" },
    debounceMs: 300,
    initialSort: { field: "expected_delivery_date", direction: "DESC" },
  })

  // ============================================================================
  // ANALYTICS DATA FETCHING
  // ============================================================================
  // Fetch status counts and delivery timeline data for charts
  // This is separate from the paginated table data

  const fetchAnalyticsData = useCallback(async () => {
    setAnalyticsLoading(true)
    try {
      const [counts, timeline] = await Promise.all([
        fetchStatusCounts(),
        fetchDeliveryTimelineData(),
      ])
      setStatusCounts(counts)
      setDeliveryData(timeline)
    } catch (error) {
      console.error("Error fetching analytics:", error)
    } finally {
      setAnalyticsLoading(false)
    }
  }, [])

  // Fetch analytics on mount
  useEffect(() => {
    fetchAnalyticsData()
  }, [fetchAnalyticsData])

  // ============================================================================
  // CHART DATA COMPUTATION
  // ============================================================================

  // Chart data: Orders by status (from pre-fetched counts)
  const statusChartData = useMemo(() => {
    const statuses = [
      "PENDING-STOCK",
      "STOCK-OK",
      "READY-TO-INVOICE",
      "ON SHIPPING",
      "INVOICED",
      "OFFER",
      "SHIPPED",
      "RETURNED",
    ]
    return statuses
      .map((status) => ({
        name: STATUS_CONFIG[status]?.label || status,
        count: statusCounts[status] || 0,
        color: STATUS_CONFIG[status]?.color || "#6b7280",
      }))
      .filter((d) => d.count > 0)
  }, [statusCounts])

  // Chart data: Orders by delivery date proximity
  const deliveryChartData = useMemo(() => {
    const today = moment().startOf("day")

    const categories = [
      { key: "overdue", label: t("Overdue"), color: "#ef4444" },
      { key: "today", label: t("Today"), color: "#f59e0b" },
      { key: "tomorrow", label: t("Tomorrow"), color: "#f97316" },
      { key: "thisWeek", label: t("This Week"), color: "#3b82f6" },
      { key: "nextWeek", label: t("Next Week"), color: "#8b5cf6" },
      { key: "later", label: t("Later"), color: "#6b7280" },
    ]

    const counts = {
      overdue: 0,
      today: 0,
      tomorrow: 0,
      thisWeek: 0,
      nextWeek: 0,
      later: 0,
    }

    deliveryData.forEach((order) => {
      if (!order.expected_delivery_date) return
      const deliveryDate = moment(order.expected_delivery_date).startOf("day")
      const daysUntil = deliveryDate.diff(today, "days")

      if (daysUntil < 0) counts.overdue++
      else if (daysUntil === 0) counts.today++
      else if (daysUntil === 1) counts.tomorrow++
      else if (daysUntil <= 7) counts.thisWeek++
      else if (daysUntil <= 14) counts.nextWeek++
      else counts.later++
    })

    return categories.map((cat) => ({
      name: cat.label,
      count: counts[cat.key],
      color: cat.color,
    }))
  }, [deliveryData, t])

  // ============================================================================
  // HANDLERS
  // ============================================================================

  // Handle status filter change
  const handleStatusFilterChange = useCallback(
    (status) => {
      setStatusFilter(status)
      // Clear date range when changing status filter
      setDateRangeFilter(null)

      // Update the paginated data filter
      // "All" means no status filter, otherwise filter by specific status
      const newStatusFilter = status === "All" ? "ALL" : status
      handleFilterChange({ statusFilter: newStatusFilter })
    },
    [handleFilterChange]
  )

  // Handle date range search
  const searchByDates = useCallback(async () => {
    if (!fromDate || !toDate) return

    // Build date range filter for FlexFlow
    const fromTimestamp = fromDate.format("YYYY-MM-DD")
    const toTimestamp = toDate.format("YYYY-MM-DD")

    // Set the date range filter which will be passed to the paginated fetch
    const dateFilter = `(expected_delivery_date>=${fromTimestamp}) AND (expected_delivery_date<=${toTimestamp})`
    setDateRangeFilter(dateFilter)

    // Update filters - this will trigger a refetch
    handleFilterChange({
      statusFilter: statusFilter === "All" ? "ALL" : statusFilter,
      dateRangeFilter: dateFilter,
    })

    // Also refresh analytics
    fetchAnalyticsData()
  }, [fromDate, toDate, statusFilter, handleFilterChange, fetchAnalyticsData])

  // Clear date filter
  const clearDateFilter = useCallback(() => {
    setDateRangeFilter(null)
    setFromDate(moment().subtract(7, "days"))
    setToDate(moment())
    handleFilterChange({
      statusFilter: statusFilter === "All" ? "ALL" : statusFilter,
      dateRangeFilter: null,
    })
  }, [statusFilter, handleFilterChange])

  // Handle refresh - refresh both table and analytics
  const handleRefresh = useCallback(() => {
    refresh()
    fetchAnalyticsData()
  }, [refresh, fetchAnalyticsData])

  // Handle sort model change from DataGrid
  const handleSortModelChange = useCallback(
    (model) => {
      if (model.length > 0) {
        handleSortChange(model[0].field, model[0].sort?.toUpperCase() || "DESC")
      }
    },
    [handleSortChange]
  )

  // Get order count for status chip (from pre-fetched counts)
  const getOrderCount = useCallback(
    (status) => {
      return statusCounts[status] || 0
    },
    [statusCounts]
  )

  // Handle authentication errors
  useEffect(() => {
    if (error && error.includes("401")) {
      localStorage.removeItem("authUser")
      navigate("/login")
    }
  }, [error, navigate])

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Compact Header: Breadcrumb + Search + Actions */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={() => navigate(-1)}
            sx={{ color: "#6b7280" }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs
            separator="/"
            sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}
          >
            <Link
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#6b7280",
                textDecoration: "none",
                fontSize: "0.813rem",
              }}
            >
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Typography
              sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#111827" }}
            >
              {t("Orders")}
            </Typography>
          </Breadcrumbs>

          {/* Total count display */}
          <Chip
            label={`${pagination.total.toLocaleString()} ${t("total")}`}
            size="small"
            sx={{
              ml: 1,
              height: 22,
              fontSize: "0.688rem",
              bgcolor: "#f3f4f6",
              color: "#6b7280",
            }}
          />
        </Stack>

        {/* Search + Date Search + Refresh */}
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          {/* Text Search Input */}
          <TextField
            size="small"
            placeholder={t("Search orders...")}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleSearchChange("")}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: 180,
              "& .MuiOutlinedInput-root": {
                height: 32,
                fontSize: "0.813rem",
              },
            }}
          />

          {/* Date Range Pickers */}
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  placeholder: t("From"),
                  sx: {
                    width: 130,
                    "& .MuiOutlinedInput-root": {
                      height: 32,
                      fontSize: "0.813rem",
                    },
                  },
                },
              }}
            />
            <Typography sx={{ color: "#9ca3af", fontSize: "0.75rem" }}>
              -
            </Typography>
            <DatePicker
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  placeholder: t("To"),
                  sx: {
                    width: 130,
                    "& .MuiOutlinedInput-root": {
                      height: 32,
                      fontSize: "0.813rem",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>

          <IconButton
            size="small"
            onClick={searchByDates}
            sx={{
              bgcolor: "#3b82f6",
              color: "#fff",
              "&:hover": { bgcolor: "#2563eb" },
              width: 32,
              height: 32,
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>

          {/* Clear date filter button (only show when date filter is active) */}
          {dateRangeFilter && (
            <IconButton
              size="small"
              onClick={clearDateFilter}
              sx={{
                color: "#ef4444",
                width: 32,
                height: 32,
              }}
              title={t("Clear date filter")}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}

          <IconButton
            size="small"
            onClick={handleRefresh}
            sx={{ color: "#6b7280", width: 32, height: 32 }}
            disabled={loading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>

      {/* Expandable Charts Panel */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => setChartsExpanded(!chartsExpanded)}
          sx={{
            px: 2,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            bgcolor: chartsExpanded ? "#f9fafb" : "#fff",
            "&:hover": { bgcolor: "#f9fafb" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <BarChartIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            <Typography
              sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#374151" }}
            >
              {t("Analytics")}
            </Typography>
            {analyticsLoading && (
              <CircularProgress size={14} sx={{ ml: 1 }} />
            )}
          </Stack>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {chartsExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: "#6b7280" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: "#6b7280" }} />
            )}
          </IconButton>
        </Box>
        <Collapse in={chartsExpanded}>
          <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
            <Grid container spacing={2}>
              {/* Orders by Status Chart */}
              <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#6b7280",
                    mb: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {t("Orders by Status")}
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={statusChartData}
                      layout="vertical"
                      margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: "#6b7280" }}
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        tick={{ fontSize: 11, fill: "#6b7280" }}
                        width={75}
                      />
                      <Tooltip
                        contentStyle={{
                          fontSize: 12,
                          borderRadius: 4,
                          border: "1px solid #e5e7eb",
                        }}
                        formatter={(value) => [value, t("Orders")]}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              {/* Orders by Delivery Date Chart */}
              <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#6b7280",
                    mb: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {t("Delivery Timeline")}
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={deliveryChartData}
                      margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: "#6b7280" }}
                      />
                      <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <Tooltip
                        contentStyle={{
                          fontSize: 12,
                          borderRadius: 4,
                          border: "1px solid #e5e7eb",
                        }}
                        formatter={(value) => [value, t("Orders")]}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {deliveryChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Status Chips - Compact Row */}
      <Box sx={{ mb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {ALL_STATUSES.map((status) => {
          const config = STATUS_CONFIG[status]
          const count = getOrderCount(status)
          const isActive = statusFilter === status
          return (
            <Chip
              key={status}
              label={`${t(config.label)} (${count})`}
              size="small"
              onClick={() => handleStatusFilterChange(status)}
              sx={{
                height: 26,
                fontSize: "0.75rem",
                fontWeight: isActive ? 600 : 400,
                bgcolor: isActive ? config.color : "#fff",
                color: isActive ? "#fff" : "#374151",
                border: `1px solid ${isActive ? config.color : "#e5e7eb"}`,
                "&:hover": {
                  bgcolor: isActive ? config.color : "#f3f4f6",
                },
              }}
            />
          )
        })}
      </Box>

      {/* Orders Table with Server-Side Pagination */}
      <MuiOrdersTable
        orders={orders}
        loading={loading}
        // Server-side pagination props
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        // Server-side sorting props
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        // Server-side mode flag
        serverSide={true}
      />

      {/* Error display */}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 1.5,
            borderRadius: 1,
            border: "1px solid #fecaca",
            bgcolor: "#fef2f2",
          }}
        >
          <Typography color="error" variant="body2">
            {t("Error loading orders")}: {error}
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

MuiOrdersList.propTypes = {
  invoices: PropTypes.array,
  onGetInvoices: PropTypes.func,
}

export default MuiOrdersList
